/**
 * Class that maintains equal minimum heights across multiple DOM elements
 * using ResizeObserver for optimal performance.
 */
export class EqualHeight {
  /**
   * Creates an instance of EqualHeight
   * @param {Element|Element[]|NodeList} elements - Single element or collection of elements
   * @throws {Error} If no elements are provided or if elements are invalid
   */
  constructor(elements) {
    if (!elements) {
      throw new Error("EqualHeight: Elements are required");
    }

    /** @private */
    this.elements =
      elements instanceof NodeList || Array.isArray(elements)
        ? Array.from(elements)
        : [elements];

    if (!this.elements.length) {
      throw new Error("EqualHeight: No valid elements provided");
    }

    if (!this.elements.every((element) => element instanceof Element)) {
      throw new Error("EqualHeight: Invalid element(s) provided");
    }

    /** @private */
    this.observer = new ResizeObserver(() => this.adjustHeights());

    this.init();
  }

  /**
   * Initializes the height adjustment and starts observing elements
   * @private
   */
  init() {
    try {
      this.elements.forEach((element) => {
        this.observer.observe(element);
      });
      this.adjustHeights();
    } catch (error) {
      console.error("EqualHeight: Initialization failed", error);
      this.destroy();
    }
  }

  /**
   * Adjusts the minimum heights of all elements to match the tallest element
   * @private
   */
  adjustHeights() {
    try {
      // Reset min-heights to get natural heights
      this.elements.forEach((element) => {
        element.style.minHeight = "auto";
      });

      // Calculate maximum height
      const maxHeight = Math.max(
        ...this.elements.map((element) => element.offsetHeight)
      );

      // Apply new min-height to all elements
      this.elements.forEach((element) => {
        element.style.minHeight = `${maxHeight}px`;
      });
    } catch (error) {
      console.error("EqualHeight: Height adjustment failed", error);
    }
  }

  /**
   * Removes observers and cleans up modifications
   * @public
   */
  destroy() {
    try {
      this.observer.disconnect();
      this.elements.forEach((element) => {
        element.style.minHeight = "";
      });
    } catch (error) {
      console.error("EqualHeight: Cleanup failed", error);
    }
  }

  /**
   * Forces a recalculation of heights
   * @public
   */
  refresh() {
    this.adjustHeights();
  }

  /**
   * Returns the current elements being observed
   * @public
   * @returns {Element[]} Array of observed elements
   */
  getElements() {
    return [...this.elements];
  }
}
